import { useEffect, useRef } from "react";
import { motion, useInView, useMotionValue, useTransform, animate } from "framer-motion";
import { Users, FlaskConical, FileCheck2, Globe2 } from "lucide-react";
import { Reveal } from "./Reveal";

const stats = [
  { icon: Users, value: 10, suffix: "+", label: "Years of Experience" },
  { icon: FlaskConical, value: 100, suffix: "+", label: "Skincare, Ayurveda & Nutraceutical Formulations" },
  { icon: FileCheck2, value: 20, suffix: "+", label: "Product Categories" },
  { icon: Globe2, value: 5, suffix: "+", label: "Global Markets Supported" },
];

const STAT_ICON_SIZE = 26;
const CARD_HOVER = { y: -6 };
const CARD_HOVER_TRANSITION = { duration: 0.35, ease: "easeOut" };

const LEAF_LEFT =
  "https://static.prod-images.emergentagent.com/jobs/9444b1cb-2cbd-41d7-b086-be1e8cd6725c/images/c00a0ea22f5b1409e86b68313ee8a3926df84a90f903571eb59360be4e3ca2fa.jpeg";
const DROPPER_RIGHT =
  "https://static.prod-images.emergentagent.com/jobs/9444b1cb-2cbd-41d7-b086-be1e8cd6725c/images/13627f222043abbcd24dfb681fab29a4e8928ba364788f4c4042675c7df14773.jpeg";

const Counter = ({ value, suffix }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const count = useMotionValue(value);
  const rounded = useTransform(count, (v) => Math.round(v).toLocaleString());

  useEffect(() => {
    count.jump(0);
  }, [count]);

  useEffect(() => {
    if (inView) animate(count, value, { duration: 1.8, ease: "easeOut" });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `animate` is a stable framer-motion module import, not component state
  }, [inView, count, value]);

  return (
    <span ref={ref}>
      <motion.span>{rounded}</motion.span>
      {suffix}
    </span>
  );
};

export default function TrustStrip() {
  return (
    <section data-testid="trust-strip" className="relative overflow-hidden bg-powder/70 border-y border-navy/10">
      <div className="pointer-events-none absolute inset-y-0 left-0 w-44 sm:w-64 lg:w-80">
        <img src={LEAF_LEFT} alt="" className="w-full h-full object-cover object-left" />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-powder/40 to-powder" />
      </div>
      <div className="pointer-events-none absolute inset-y-0 right-0 w-44 sm:w-64 lg:w-80">
        <img src={DROPPER_RIGHT} alt="" className="w-full h-full object-cover object-right" />
        <div className="absolute inset-0 bg-gradient-to-l from-transparent via-powder/40 to-powder" />
      </div>

      <div className="relative max-w-5xl mx-auto px-6 lg:px-10 py-14 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
        {stats.map(({ icon: Icon, value, suffix, label }, i) => (
          <Reveal key={label} delay={i * 0.1}>
            <motion.div
              whileHover={CARD_HOVER}
              transition={CARD_HOVER_TRANSITION}
              className="h-full flex flex-col items-center text-center bg-cream/95 rounded-xl px-4 sm:px-5 py-7 shadow-[0_12px_32px_rgba(62,74,46,0.10)]"
            >
              <span className="text-navy">
                <Icon size={STAT_ICON_SIZE} strokeWidth={1.3} />
              </span>
              <p className="mt-3 font-display font-semibold text-3xl sm:text-4xl text-navy leading-none">
                <Counter value={value} suffix={suffix} />
              </p>
              <p className="mt-3 text-[10px] sm:text-[11px] uppercase tracking-[0.12em] font-semibold text-charcoal/65 leading-relaxed max-w-[180px]">
                {label}
              </p>
            </motion.div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}
